<script setup lang="ts">
import { computed } from 'vue';

// 导入 SVG 图标
import CircleErrorIcon from '../../assets/circle-error.svg?component';
import CircleSuccessIcon from '../../assets/circle-success.svg?component';
import CircleWarningIcon from '../../assets/circle-warning.svg?component';
import CloseIcon from '../../assets/close.svg?component';

import type { MessageProps } from './types';

const props = withDefaults(defineProps<MessageProps>(), {
  variant: 'info',
  showIcon: true,
  closeable: false,
});

defineEmits<{
  close: [];
}>();

// 图表路径
const iconSrc = computed(() => {
  switch (props.variant) {
    case 'info':
    case 'warning':
      return CircleWarningIcon;
    case 'success':
      return CircleSuccessIcon;
    case 'error':
      return CircleErrorIcon;
  }
});

// 计算消息样式类
const messageClasses = computed(() => {
  const baseClass = 'message';
  return [baseClass, props.class, props.variant].filter(Boolean).join(' ');
});
</script>

<template>
  <div :class="messageClasses" role="alert">
    <div class="message__content">
      <!-- 图标 -->
      <slot name="icon" v-if="showIcon">
        <component :is="iconSrc" class="message__icon" />
      </slot>
      <!-- 标题 -->
      <div v-if="title || $slots.title" class="message__title">
        <slot name="title">{{ title }}</slot>
      </div>
    </div>

    <!-- 关闭按钮 -->
    <slot name="close" v-if="closeable">
      <button class="message__close" @click="$emit('close')" aria-label="关闭">
        <CloseIcon class="message__close-icon" />
      </button>
    </slot>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/mixins.scss' as *;

/* 消息基础样式 */
.message {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: var(--x-radius-medium);
  border: 1px solid var(--x-back-4);
  background-color: var(--x-back-1);
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  color: var(--x-text-1);
  /* 15px 因为是内边距所以给15px */
  padding: 9px 15px;
  justify-content: space-between;
  align-items: center;
  box-shadow: var(--x-shadow);
  width: fit-content;
  max-width: 70vw;

  @include mobile {
    padding: responsive-size(9px) responsive-size(15px);
  }

  &.info .message__icon {
    color: var(--x-purple-6);
  }

  &.success .message__icon {
    color: var(--x-success-6);
  }

  &.warning .message__icon {
    color: var(--x-warning-6);
  }

  &.error .message__icon {
    color: var(--x-error-6);
  }
}

/* 图标样式 */
.message__icon {
  height: 16px;
  width: 16px;
  flex-shrink: 0;
  margin-top: calc((var(--x-text-14-line-height) - 16px) / 2);
}

/* 内容区域 */
.message__content {
  flex: none;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  color: var(--x-text-1);
}

.message__title {
  // 正常换行，允许在 flex 容器中收缩
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* 关闭按钮样式 */
.message__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;

  color: var(--x-text-3);
  cursor: pointer;
  border-radius: var(--x-radius-small);
  flex-shrink: 0;
  transition: all 0.2s ease;
  &:deep(svg) {
    width: 16px;
    height: 16px;
  }
}

.message__close:hover {
  color: var(--x-text-1);
  background: var(--x-back-3);
}
</style>
